import { ExportColumn } from './exportResponseToCSV';

type Selection = Record<string, boolean>;
// Relationship columns vary per survey, so they persist separately from the columns every survey shares.
type Scope = 'common' | 'relationships';

const SCOPES: Scope[] = ['common', 'relationships'];
const EMPTY_SELECTIONS: Record<Scope, Selection> = { common: {}, relationships: {} };

const isBooleanEntry = (entry: [string, unknown]): entry is [string, boolean] => typeof entry[1] === 'boolean';

const readSelection = (key: string): Selection => {
  let value: unknown;
  try {
    value = JSON.parse(localStorage.getItem(key) ?? '{}');
  } catch {
    return {};
  }
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  return Object.fromEntries(Object.entries(value).filter(isBooleanEntry));
};

const scopeOf = (column: ExportColumn): Scope => (column.id.startsWith('relationship.') ? 'relationships' : 'common');

const preferenceKeys = (userId: string, surveyId: string): Record<Scope, string> => {
  const common = `placeplot:csv-columns:v1:${encodeURIComponent(userId)}`;
  return { common, relationships: `${common}:survey:${encodeURIComponent(surveyId)}` };
};

const readSelections = (userId: string | undefined, surveyId: string): Record<Scope, Selection> => {
  if (!userId) return EMPTY_SELECTIONS;
  const keys = preferenceKeys(userId, surveyId);
  return { common: readSelection(keys.common), relationships: readSelection(keys.relationships) };
};

export const loadExportSelection = (columns: ExportColumn[], userId: string | undefined, surveyId: string): string[] => {
  const stored = readSelections(userId, surveyId);
  const isSelected = (column: ExportColumn): boolean => stored[scopeOf(column)][column.id] !== false;
  return columns.filter(isSelected).map((column) => column.id);
};

export const saveExportSelection = (columns: ExportColumn[], selectedIds: string[], userId: string | undefined, surveyId: string): void => {
  if (!userId) return;
  const keys = preferenceKeys(userId, surveyId);
  const selected = new Set(selectedIds);
  const selections: Record<Scope, Selection> = { common: {}, relationships: {} };
  columns.forEach((column) => {
    selections[scopeOf(column)][column.id] = selected.has(column.id);
  });
  try {
    SCOPES.forEach((scope) => localStorage.setItem(keys[scope], JSON.stringify(selections[scope])));
  } catch {
    // Storage restrictions must not prevent a CSV download.
  }
};
